import React, { useEffect, useState } from "react";
import { TopBar } from "@/components/TopBar";
import { EmptyState } from "@/components/EmptyState";
import { NoteIcon, RoastDiamonds } from "@/components/Flavor";
import { useI18n } from "@/i18n";
import { useRouter } from "@/router/Router";
import { useBackButton } from "@/lib/hooks";
import { fetchTaste, mediaUrl, voteTaste, type TasteProfile } from "@/lib/api";
import { haptic } from "@/lib/telegram";
import type { FlavorNote } from "@/types";

/** "Мой вкус": the customer's coffee character, built from their thumbs up/down. */
export function TastePage() {
  const { t, locale } = useI18n();
  const { goBack, navigate } = useRouter();
  useBackButton(goBack);
  const [data, setData] = useState<TasteProfile | null | undefined>(undefined);
  const load = () => fetchTaste().then(setData).catch(() => setData(null));
  useEffect(() => {
    load();
  }, [locale]);

  const vote = async (productId: string, liked: boolean) => {
    haptic("selection");
    await voteTaste(productId, liked).catch(() => {});
    load();
  };

  if (data === undefined) return (<><TopBar title={t("taste.title")} onBack={goBack} /><main className="page page--no-nav">{t("common.loading")}</main></>);
  if (data === null) return (<><TopBar title={t("taste.title")} onBack={goBack} /><main className="page page--no-nav"><EmptyState art="generic" title={t("common.error")} /></main></>);

  const bar = (v: number | null) => (
    <div className="flavor-bar">
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={v !== null && i <= Math.round(v) ? "on" : ""} />
      ))}
    </div>
  );
  const left = Math.max(0, data.minLikes - data.likes);

  return (
    <>
      <TopBar title={t("taste.title")} onBack={goBack} />
      <main className="page page--no-nav">
        <div className="taste-hero">
          <span className="eyebrow">{t("taste.eyebrow")}</span>
          {data.archetype ? (
            <>
              <strong>{t(`taste.archetypes.${data.archetype}.name`)}</strong>
              <span>{t(`taste.archetypes.${data.archetype}.text`)}</span>
            </>
          ) : (
            <>
              <strong>{t("taste.notYet")}</strong>
              <span>{t("taste.notYetText", { n: left })}</span>
            </>
          )}
        </div>

        {data.toRate.length > 0 && (
          <>
            <div className="section-heading"><span className="diamond" />{t("taste.rateTitle")}</div>
            {data.toRate.map((p) => (
              <div key={p.id} className="taste-rate">
                <button className="taste-rate__img" onClick={() => navigate({ name: "product", slug: p.slug })}>
                  {p.images[0] ? <img src={mediaUrl(p.images[0], 200)} alt="" /> : null}
                </button>
                <span className="taste-rate__name">
                  <b>{p.name}</b>
                  <small>{t("taste.liked")}</small>
                </span>
                <button className="taste-btn" onClick={() => vote(p.id, true)} aria-label={t("taste.yes")}>👍</button>
                <button className="taste-btn" onClick={() => vote(p.id, false)} aria-label={t("taste.no")}>👎</button>
              </div>
            ))}
          </>
        )}

        {data.likes > 0 && (
          <>
            <div className="section-heading"><span className="diamond" />{t("taste.whatYouLike")}</div>
            {data.roast !== null && (
              <div className="taste-roast">
                <span>{t("taste.roast")}</span>
                <RoastDiamonds level={Math.round(data.roast)} withLabel />
              </div>
            )}
            <div className="flavor-scales" style={{ marginBottom: 16 }}>
              {(["acidity", "sweetness", "body", "bitterness"] as const).map((k) =>
                data.scales[k] === null ? null : (
                  <React.Fragment key={k}>
                    <span>{t(`flavor.${k}`)}</span>
                    {bar(data.scales[k])}
                  </React.Fragment>
                ),
              )}
            </div>
            {data.notes.length > 0 && (
              <div className="notes">
                {data.notes.map((n) => (
                  <span key={n.note} className="note">
                    <NoteIcon note={n.note as FlavorNote} />
                    {t(`flavor.notes.${n.note}`)} · {n.count}
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        {data.likes === 0 && data.toRate.length === 0 && (
          <EmptyState art="generic" title={t("taste.emptyTitle")} hint={t("taste.emptyText")} />
        )}
        <p className="hint-line" style={{ marginTop: 16 }}>{t("taste.privacy", { likes: data.likes, dislikes: data.dislikes })}</p>
      </main>
    </>
  );
}
